import { cliente } from "../http/cliente";
import type {
  ConfirmarConciliacaoIn,
  DesfazerConciliacaoIn,
  EmpresaConciliavel,
  FilaStatus,
  ItemFilaConciliacao,
  MovimentoConciliacao,
  MovimentoConciliado,
} from "./tipos";

/*
 * Fetchers da conciliacao bancaria assistida (FE-3 CP-A — ADR 0026).
 *
 * Camada fina sobre o `cliente` HTTP (CP-C): injeta Bearer, trata refresh/401/503
 * e normaliza erro em `ErroHttp`. Estes fetchers NAO tratam erro — propagam a
 * rejeicao do `cliente` para o TanStack Query (hooks em `./hooks`) decidir.
 *
 * `baseURL` do cliente ja e `/api`, entao os paths aqui sao relativos a ela.
 */

/** `GET /api/conciliacao/empresas` — empresas conciliaveis pelo escopo do usuario. */
export async function buscarEmpresasConciliaveis(): Promise<
  EmpresaConciliavel[]
> {
  const resposta =
    await cliente.get<EmpresaConciliavel[]>("/conciliacao/empresas");
  return resposta.data;
}

/** `GET /api/empresas/{empresaId}/conciliacao/fila?status=<status>`. */
export async function buscarFilaConciliacao(
  empresaId: string,
  status: FilaStatus,
): Promise<ItemFilaConciliacao[]> {
  const resposta = await cliente.get<ItemFilaConciliacao[]>(
    `/empresas/${empresaId}/conciliacao/fila`,
    { params: { status } },
  );
  return resposta.data;
}

/**
 * `POST /api/empresas/{empresaId}/conciliacao/fila/{filaId}/confirmar`.
 *
 * Confirma a conciliacao manual de um item da fila contra um candidato escolhido
 * (`corpo.candidato_id`). Devolve o `MovimentoConciliacao` criado.
 */
export async function confirmarConciliacao(
  empresaId: string,
  filaId: string,
  corpo: ConfirmarConciliacaoIn,
): Promise<MovimentoConciliacao> {
  const resposta = await cliente.post<MovimentoConciliacao>(
    `/empresas/${empresaId}/conciliacao/fila/${filaId}/confirmar`,
    corpo,
  );
  return resposta.data;
}

/** Parametros de paginacao do `GET .../conciliacao/movimentos`. */
interface FiltrosMovimentos {
  status: string;
  limit: number;
  offset: number;
}

/**
 * `GET /api/empresas/{empresaId}/conciliacao/movimentos?status=&limit=&offset=`
 * (FE-4 — gapB). Lista os movimentos JA conciliados (array PURO, sem total).
 */
export async function buscarMovimentos(
  empresaId: string,
  filtros: FiltrosMovimentos,
): Promise<MovimentoConciliado[]> {
  const resposta = await cliente.get<MovimentoConciliado[]>(
    `/empresas/${empresaId}/conciliacao/movimentos`,
    { params: filtros },
  );
  return resposta.data;
}

/**
 * `POST /api/empresas/{empresaId}/conciliacao/movimentos/{movimentoId}/desfazer`
 * (FE-4). Reverte o movimento e recalcula a projecao; devolve o
 * `MovimentoConciliacao` com status "desfeita".
 */
export async function desfazerConciliacao(
  empresaId: string,
  movimentoId: string,
  corpo: DesfazerConciliacaoIn,
): Promise<MovimentoConciliacao> {
  const resposta = await cliente.post<MovimentoConciliacao>(
    `/empresas/${empresaId}/conciliacao/movimentos/${movimentoId}/desfazer`,
    corpo,
  );
  return resposta.data;
}
